import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import {
	Box,
	CircularProgress,
	FormControlLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Stack
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import type { Option } from "../constants";
import AssignmentValueField from "../fields/AssignmentValueField";
import ComparativeOperatorsSelect from "../fields/ComparativeOperatorsSelect";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export type QuerySortOrder = "Ascending" | "Descending";
export type QueryRecordChoice = "firstRecord" | "allRecords";

export interface QueryCondition {
	id: string;
	databaseTableFieldId: string;
	databaseTableFieldName: string;
	operator: string;
	value: string;
}

export interface QueryBuilderFormState {
	queryName: string;
	databaseTableId: string;
	databaseTableName: string;
	sortByFieldId: string;
	sortOrder: QuerySortOrder;
	recordChoice: QueryRecordChoice;
	conditions: QueryCondition[];
}

export interface QueryBuilderFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: QueryBuilderFormState) => void;
	initialState?: Partial<QueryBuilderFormState>;
	platformId: string | number;
	operatorOptions?: Option[];
	valueVariables?: Option[];
	valueScreenFields?: Option[];
	valueDatabaseTables?: Option[];
	isSaving?: boolean;
	suppressComparativeOperatorsAutoFetch?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
}

interface DatabaseTableApiOption {
	id: string;
	name: string;
}

interface DatabaseFieldApiOption {
	id: string;
	name: string;
}

const EMPTY_OPTIONS: Option[] = [];

const QueryBuilderForm = (props: QueryBuilderFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		platformId,
		operatorOptions,
		valueVariables = EMPTY_OPTIONS,
		valueScreenFields = EMPTY_OPTIONS,
		valueDatabaseTables = EMPTY_OPTIONS,
		isSaving = false,
		suppressComparativeOperatorsAutoFetch = false,
		getDuplicateNameError,
	} = props;

	const authHeader = useAuthHeader();

	const [tableOptions, setTableOptions] = useState<DatabaseTableApiOption[]>([]);
	const [sortFieldOptions, setSortFieldOptions] = useState<DatabaseFieldApiOption[]>([]);
	const [isLoadingTables, setIsLoadingTables] = useState(false);
	const [isLoadingFields, setIsLoadingFields] = useState(false);

	const conditionFieldOptions = useMemo<Option[]>(
		() => sortFieldOptions.map((f) => ({ label: f.name, value: f.id })),
		[sortFieldOptions]
	);
	const resolvedOperatorOptions = operatorOptions ?? [];

	const buildCondition = (overrides?: Partial<QueryCondition>): QueryCondition => ({
		id: overrides?.id ?? uuidv4(),
		databaseTableFieldId: overrides?.databaseTableFieldId ?? "",
		databaseTableFieldName: overrides?.databaseTableFieldName ?? "",
		operator: overrides?.operator ?? resolvedOperatorOptions[0]?.value ?? "",
		value: overrides?.value ?? "",
	});

	const [formState, setFormState] = useState<QueryBuilderFormState>({
		queryName: "",
		databaseTableId: "",
		databaseTableName: "",
		sortByFieldId: "",
		sortOrder: "Descending",
		recordChoice: "firstRecord",
		conditions: [buildCondition()]
	});

	const fetchFieldsForTable = async (tableId: string) => {
		if (!tableId) {
			setSortFieldOptions([]);
			return;
		}
		setIsLoadingFields(true);
		try {
			const response = await axios.get(
				apiRoutes.getDatabaseTableFields(tableId),
				{ headers: authHeader }
			);
			const fields: DatabaseFieldApiOption[] = (response.data ?? []).map(
				(f: { id: number; name: string }) => ({
					id: String(f.id),
					name: f.name
				})
			);
			setSortFieldOptions(fields);
			setFormState((prev) => ({
				...prev,
				conditions: prev.conditions.map((c) => {
					if (c.databaseTableFieldId && !c.databaseTableFieldName) {
						const match = fields.find((f) => f.id === c.databaseTableFieldId);
						return match ? { ...c, databaseTableFieldName: match.name } : c;
					}
					return c;
				}),
			}));
		} catch (error) {
			console.error("Error fetching table fields:", error);
			setSortFieldOptions([]);
		} finally {
			setIsLoadingFields(false);
		}
	};

	useEffect(() => {
		if (!open || !platformId) return;
		let cancelled = false;

		const load = async () => {
			setIsLoadingTables(true);
			setSortFieldOptions([]);
			try {
				const response = await axios.get(
					apiRoutes.getDatabaseTableOptions(platformId),
					{ headers: authHeader }
				);
				if (cancelled) return;
				const tables: DatabaseTableApiOption[] = (response.data ?? []).map(
					(t: { id: number; name: string }) => ({
						id: String(t.id),
						name: t.name
					})
				);
				setTableOptions(tables);

				setFormState({
					queryName: initialState?.queryName ?? "",
					databaseTableId: initialState?.databaseTableId ?? "",
					databaseTableName: initialState?.databaseTableName ?? "",
					sortByFieldId: initialState?.sortByFieldId ?? "",
					sortOrder: initialState?.sortOrder ?? "Descending",
					recordChoice: initialState?.recordChoice ?? "firstRecord",
					conditions: initialState?.conditions?.length
						? initialState.conditions.map((c) => buildCondition(c))
						: [buildCondition()]
				});

				if (initialState?.databaseTableId) {
					fetchFieldsForTable(initialState.databaseTableId);
				}
			} catch (error) {
				if (!cancelled) {
					console.error("Error fetching database tables:", error);
					setTableOptions([]);
					setFormState({
						queryName: initialState?.queryName ?? "",
						databaseTableId: initialState?.databaseTableId ?? "",
						databaseTableName: initialState?.databaseTableName ?? "",
						sortByFieldId: initialState?.sortByFieldId ?? "",
						sortOrder: initialState?.sortOrder ?? "Descending",
						recordChoice: initialState?.recordChoice ?? "firstRecord",
						conditions: initialState?.conditions?.length
							? initialState.conditions.map((c) => buildCondition(c))
							: [buildCondition()]
					});
				}
			} finally {
				if (!cancelled) setIsLoadingTables(false);
			}
		};

		load();
		return () => { cancelled = true; };
	}, [open, platformId]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.queryName),
		[getDuplicateNameError, formState.queryName],
	);

	const isSaveDisabled =
		!formState.queryName.trim() ||
		Boolean(duplicateNameError) ||
		!formState.databaseTableId ||
		!formState.sortByFieldId ||
		isLoadingTables ||
		isLoadingFields ||
		formState.conditions.some((condition) => !condition.databaseTableFieldId || !condition.operator || !condition.value.trim());

	const updateCondition = (id: string, updates: Partial<Omit<QueryCondition, "id">>) => {
		setFormState((previous) => ({
			...previous,
			conditions: previous.conditions.map((condition) => (
				condition.id === id ? { ...condition, ...updates } : condition
			))
		}));
	};

	const handleRemoveCondition = (id: string) => {
		setFormState((previous) => {
			if (previous.conditions.length === 1) {
				return previous;
			}
			return {
				...previous,
				conditions: previous.conditions.filter((condition) => condition.id !== id)
			};
		});
	};

	const handleAddCondition = () => {
		setFormState((previous) => ({
			...previous,
			conditions: [...previous.conditions, buildCondition()]
		}));
	};

	const handleTableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const tableId = event.target.value;
		const selectedTable = tableOptions.find((t) => t.id === tableId);
		setFormState((previous) => ({
			...previous,
			databaseTableId: tableId,
			databaseTableName: selectedTable?.name ?? "",
			sortByFieldId: "",
			conditions: [buildCondition()],
		}));
		fetchFieldsForTable(tableId);
	};

	const handleSave = () => {
		onSave({
			...formState,
			queryName: formState.queryName.trim()
		});
	};

	const renderConditionRows = () => (
		<Stack spacing={2}>
			{formState.conditions.map((condition, index) => (
				<Stack
					direction="row"
					sx={{ mb: 1, alignItems: "flex-start" }}
					key={condition.id}
				>
					<Box
						sx={{
							minWidth: 24,
							textAlign: "center",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "text.secondary",
							fontWeight: 500,
							paddingTop: 4
						}}
					>
						{ index + 1 }
					</Box>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							borderRadius: 2,
							border: "1px solid",
							borderColor: "divider",
							backgroundColor: "background.paper",
							padding: 2,
							width: "100%"
						}}
					>
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center", justifyContent: "flex-start", width: "100%" }}
						>
							<CanvasFormTextField
								select
								fullWidth
								label="Field"
								value={isLoadingFields || !conditionFieldOptions.length ? "" : condition.databaseTableFieldId}
								onChange={(event) => {
									const selectedId = event.target.value;
									const selectedName = conditionFieldOptions.find((o) => o.value === selectedId)?.label ?? "";
									updateCondition(condition.id, { databaseTableFieldId: selectedId, databaseTableFieldName: selectedName });
								}}
								disabled={!formState.databaseTableId || isLoadingFields || !conditionFieldOptions.length}
								width="100%"
							>
								{isLoadingFields ? (
									<MenuItem disabled>
										<CircularProgress size={16} sx={{ mr: 1 }} />Loading...
									</MenuItem>
								) : !conditionFieldOptions.length ? (
									<MenuItem disabled sx={{ fontStyle: "italic" }}>
										{formState.databaseTableId ? "No fields available" : "Select a table first"}
									</MenuItem>
								) : (
									conditionFieldOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))
								)}
							</CanvasFormTextField>
							<ComparativeOperatorsSelect
								value={condition.operator}
								onChange={(value) => updateCondition(condition.id, { operator: value })}
								options={resolvedOperatorOptions}
								suppressAutoFetch={suppressComparativeOperatorsAutoFetch}
							/>
							<AssignmentValueField
								value={condition.value}
								onChange={(val) => updateCondition(condition.id, { value: val })}
								variables={valueVariables}
								screenFields={valueScreenFields}
								databaseTables={valueDatabaseTables}
							/>
							<RaisedTrashIconButton
								onClick={() => handleRemoveCondition(condition.id)}
								disabled={formState.conditions.length === 1}
								aria-label={`Remove condition ${index + 1}`}
							/>
						</Stack>
					</Box>
				</Stack>
			))}
		</Stack>
	);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Query Builder"
			icon={<FormatListBulleted />}
			iconColor="#3F77E8"
			gradient="linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Query"
			width={520}
		>
			<Stack direction="row" spacing={2} sx={{ p: 2 }}>
				<CanvasFormTextField
					label="Query Name"
					value={formState.queryName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) => setFormState((previous) => ({
						...previous,
						queryName: event.target.value
					}))}
					required
					disabled={isLoadingTables}
					width="50%"
				/>
				<CanvasFormTextField
					select
					label="Database Table"
					value={isLoadingTables || !tableOptions.length ? "" : formState.databaseTableId}
					onChange={handleTableChange}
					disabled={isLoadingTables || !tableOptions.length}
					width="50%"
				>
					{isLoadingTables ? (
						<MenuItem disabled>
							<CircularProgress size={16} sx={{ mr: 1 }} />Loading...
						</MenuItem>
					) : !tableOptions.length ? (
						<MenuItem disabled sx={{ fontStyle: "italic" }}>No tables available</MenuItem>
					) : (
						tableOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								{option.name}
							</MenuItem>
						))
					)}	
				</CanvasFormTextField>
			</Stack>
			<Stack direction="row" spacing={2} sx={{ p: 2 }}>
				<CanvasFormTextField
					select
					label="Sort By"
					value={isLoadingFields || !sortFieldOptions.length ? "" : formState.sortByFieldId}
					onChange={(event) => setFormState((previous) => ({
						...previous,
						sortByFieldId: event.target.value
					}))}
					disabled={!formState.databaseTableId || isLoadingFields || !sortFieldOptions.length}
					width="50%"
				>
					{isLoadingFields ? (
						<MenuItem disabled>
							<CircularProgress size={16} sx={{ mr: 1 }} />Loading...
						</MenuItem>
					) : !sortFieldOptions.length ? (
						<MenuItem disabled sx={{ fontStyle: "italic" }}>
							{formState.databaseTableId ? "No fields available" : "Select a table first"}
						</MenuItem>
					) : (
						sortFieldOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								{option.name}
							</MenuItem>
						))
					)}
				</CanvasFormTextField>
				<CanvasFormTextField
					select
					label="Sort Order"
					value={formState.sortOrder}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							sortOrder: event.target.value as QuerySortOrder
						}))
					}
					width="50%"
				>
					{["Ascending", "Descending"].map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
				</CanvasFormTextField>
			</Stack>
			<Box sx={{ p: 2 }}>
				<RadioGroup
					row
					value={formState.recordChoice}
					onChange={(event) => setFormState((previous) => ({
						...previous,
						recordChoice: event.target.value as QueryRecordChoice
					}))}
				>
					<FormControlLabel value="firstRecord" control={<Radio />} label="Only First Record" />
					<FormControlLabel value="allRecords" control={<Radio />} label="All Records" />
				</RadioGroup>
			</Box>
			<Box sx={{ p: 2 }}>
				{renderConditionRows()}
				<Box sx={{ textAlign: "center", marginTop: 2 }}>
					<AddConditionButton label="Add Condition" onClick={handleAddCondition} />
				</Box>
			</Box>
		</BaseForm>
	);
};

export default QueryBuilderForm;
